import { z } from 'zod';
import { Entity } from '../../entities/entity'
import { PrepaymentSetting } from '../settings';
import { AdjustmentType, Lex, LexoRank } from '../shared';
import { Contact } from '../contact';
import { QuotePro } from '../quotes';
import { Policy, PolicyNotification, PolicyOptionVisibility } from './policy';

export enum QuotePolicyReminderType {
    Acceptance = 'Acceptance',
    Payment = 'Payment',
}

export enum QuotePolicyDates {
    SendDate = 'SendDate',
    ExpiryDate = 'ExpiryDate',
    AcceptedDate = 'AcceptedDate',
}

export enum QuotePolicyPrepaymentRuleOperator {
    LessThan = 'LessThan',
    LessThanOrEqual = 'LessThanOrEqual',
    // Equal = 'Equal',
    GreaterThanOrEqual = 'GreaterThanOrEqual',
    GreaterThan = 'GreaterThan',
}

export enum QuotePolicyPrepaymentRuleType {
    Percentage = 'Percentage',
    Amount = 'Amount',
}

export enum QuotePolicyPrepaymentSkipType {
    NotApplicable = 'Not Applicable',
    Include = 'Include',
    Exclude = 'Exclude',
}

export enum QuotePolicyJobConvertSplitType {
    //   None = 'None',
    JobTemplate = 'Job Template',
    ItemCategory = 'Item Category',
    QuoteSection = 'Quote Section'
}

export enum QuotePolicyNotificationType {
    QuoteAccepted = 'Quote Accepted',
    QuoteDeclined = 'Quote Declined',
    QuotePrepaid = 'Quote Prepaid',
}

export interface QuotePolicyAcceptance {
    _id: string;
    poNumber: PolicyOptionVisibility;
    attachments: PolicyOptionVisibility;
    signature: PolicyOptionVisibility;
    allowOnlineAcceptance: boolean;
    // allowOnlinePayment: boolean;
}

export interface QuotePolicyJobConversion {
    _id: string;
    autoConvert: boolean;
    copyActivities: boolean;
    split: QuotePolicyJobConvertSplitType;
    jobTemplate: Entity;
}

export interface QuotePolicyReminder {
    _id: string;
    active: boolean;
    type: QuotePolicyReminderType,
    date: QuotePolicyDates,
    offset: number;
    message: string;
    subject: string;
}

export interface QuotePolicyReminders {
    _id: string;
    enableForAcceptance: boolean;
    acceptance: QuotePolicyReminder[];

    enableForPayment: boolean;
    payment: QuotePolicyReminder[];
}

export type QuotePolicyNotification = PolicyNotification<QuotePolicyNotificationType>;

export interface QuotePolicyNotifications {
    _id: string;
    enabled: boolean;
    accept: QuotePolicyNotification;
    decline: QuotePolicyNotification;
    prepaid: QuotePolicyNotification;
}

export interface QuotePolicyPrepaymentRule {
    _id: string;
    quoteValue: number;
    operator: QuotePolicyPrepaymentRuleOperator;
    prepaymentType: QuotePolicyPrepaymentRuleType;
    amount: number;
    sortOrder: Lex | any;
    active: boolean;
}

export interface QuotePolicyPrepayments {
    _id: string;
    enabled: boolean;
    allowOverride: boolean;
    rules: QuotePolicyPrepaymentRule[];
    settings: PrepaymentSetting
}

export interface QuotePolicyDefaults {
    _id: string;
    adjustmentType: AdjustmentType;
    expiryDays: number;
}

export interface QuotePolicy extends Policy {
    acceptance: QuotePolicyAcceptance;
    jobConversion: QuotePolicyJobConversion;
    prepayments: QuotePolicyPrepayments;
    reminders: QuotePolicyReminders;
    notifications: QuotePolicyNotifications;
    defaults: QuotePolicyDefaults;
}

export const defaultQuotePolicy: QuotePolicy = {
    _id: null,
    isDefault: false,
    active: true,
    name: '',
    __v: 0,
    updatedBy: null,
    createdBy: null,
    // pricing: {
    //     _id: null,
    //     costSource: CostSource.StandardCost,
    //     pricingMethod: PricingMethod.Margin,
    //     multiplier: 30,
    //     priceList: null
    // },
    acceptance: {
        _id: null,
        poNumber: PolicyOptionVisibility.Disabled,
        attachments: PolicyOptionVisibility.Disabled,
        signature: PolicyOptionVisibility.Disabled,
        allowOnlineAcceptance: true,
    },
    jobConversion: {
        _id: null,
        autoConvert: false,
        copyActivities: false,
        split: QuotePolicyJobConvertSplitType.ItemCategory,
        jobTemplate: null
    },
    prepayments: {
        _id: null,
        enabled: false,
        allowOverride: false,
        rules: [],
        settings: null
    },
    reminders: {
        _id: null,
        enableForAcceptance: false,
        acceptance: [],
        enableForPayment: false,
        payment: []
    },
    notifications: {
        _id: null,
        enabled: false,
        accept: null,
        decline: null,
        prepaid: null
    },
    defaults: {
        _id: null,
        adjustmentType: 'Percent',
        expiryDays: 30
    },
}

function canProcessPrepaymentRule(rule: QuotePolicyPrepaymentRule, quote: QuotePro) {

    const schema = z.object({
        amount: z.number().min(0),
        quoteValue: z.number().min(0),
        totalSellEx: z.number().min(0),
    });

    return schema.safeParse({
        amount: rule.amount,
        quoteValue: rule.quoteValue,
        totalSellEx: quote.totalSellEx
    });
}

function calculateRulePrepayment(rule: QuotePolicyPrepaymentRule, quote: QuotePro) {
    if (!canProcessPrepaymentRule(rule, quote)) return 0;

    switch (rule.prepaymentType) {
        case QuotePolicyPrepaymentRuleType.Amount:
            return rule.amount;
        case QuotePolicyPrepaymentRuleType.Percentage:
            return (quote.totalSellInc / 100) * rule.amount;
        default:
            return 0;
    }
}

const quotePolicyPrepaymentHelper = {
    // [QuotePolicyPrepaymentRuleOperator.Equal]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
    //     return quote.totalSellEx === rule.quoteValue
    //         ? calculateRulePrepayment(rule, quote)
    //         : 0;
    // },

    [QuotePolicyPrepaymentRuleOperator.GreaterThan]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc > rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    },

    [QuotePolicyPrepaymentRuleOperator.GreaterThanOrEqual]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc >= rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    },

    [QuotePolicyPrepaymentRuleOperator.LessThan]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc < rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    },

    [QuotePolicyPrepaymentRuleOperator.LessThanOrEqual]: (rule: QuotePolicyPrepaymentRule, quote: QuotePro) => {
        return quote.totalSellInc <= rule.quoteValue
            ? calculateRulePrepayment(rule, quote)
            : 0;
    }


}

export const calculateQuotePrepayment = (quote: QuotePro) => {

    let prepaymentValue = 0;

    if (!quote?.policy?.prepayments?.enabled)
        return prepaymentValue;

    for (const rule of quote?.policy?.prepayments?.rules) {
        prepaymentValue = quotePolicyPrepaymentHelper[rule.operator](rule, quote);

        if (prepaymentValue > 0)
            break;
    }

    return prepaymentValue;
}